import _ from "lodash";
import { ReactComponent as MeetingDefaultIcon } from "pages/Product/FeaturesSection/icons/butterfly-line_32.svg";
import { ReactComponent as MeetingActiveIcon } from "pages/Product/FeaturesSection/icons/butterfly-solid_32.svg";
import { ReactComponent as DeliverableDefaultIcon } from "pages/Product/FeaturesSection/icons/ovals-line_32.svg";
import { ReactComponent as DeliverableActiveIcon } from "pages/Product/FeaturesSection/icons/ovals-solid_32.svg";
import { ReactComponent as ContractDefaultIcon } from "pages/Product/FeaturesSection/icons/rect-line_32.svg";
import { ReactComponent as ContractActiveIcon } from "pages/Product/FeaturesSection/icons/rect-solid_32.svg";
import { ReactComponent as NotificationDefaultIcon } from "pages/Product/FeaturesSection/icons/rhombus-line_32.svg";
import { ReactComponent as NotificationActiveIcon } from "pages/Product/FeaturesSection/icons/rhombus-solid_32.svg";
import { ReactComponent as FeedbackDefaultIcon } from "pages/Product/FeaturesSection/icons/sector-line_32.svg";
import { ReactComponent as FeedbackActiveIcon } from "pages/Product/FeaturesSection/icons/sector-solid_32.svg";
import { ReactComponent as InvoiceDefaultIcon } from "pages/Product/FeaturesSection/icons/semi-circle-line_32.svg";
import { ReactComponent as InvoiceActiveIcon } from "pages/Product/FeaturesSection/icons/semi-circle-solid_32.svg";
import { ReactComponent as RoadmapDefaultIcon } from "pages/Product/FeaturesSection/icons/steps-line_32.svg";
import { ReactComponent as RoadmapActiveIcon } from "pages/Product/FeaturesSection/icons/steps-solid_32.svg";
import { ReactComponent as InquiryDefaultIcon } from "pages/Product/FeaturesSection/icons/triangle-line_32.svg";
import { ReactComponent as InquiryActiveIcon } from "pages/Product/FeaturesSection/icons/triangle-solid_32.svg";
import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import helpers from "styles/helpers";
import { mediaMin } from "styles/media";

const NavigationWrapper = styled.div<{ $visible: boolean }>`
  display: none;
  position: fixed;
  top: 50%;
  left: ${({ $visible }) => ($visible ? 0 : "-172px")};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(-50%);
  width: 172px;
  padding-left: 48px;

  ${helpers.transition("opacity", "left")};

  ${mediaMin.xlg`
    display: block;
  `};
`;

const DefaultIcon = styled.div`
  width: 32px;
  height: 32px;
  position: absolute;
  left: 0;

  ${helpers.transition("opacity")};
`;

const ActiveIcon = styled.div`
  width: 32px;
  height: 32px;
  position: absolute;
  left: 0;

  ${helpers.transition("opacity")};
`;

const NavigationItem = styled.div<{ $isActive: boolean }>`
  height: 32px;
  width: 100%;
  display: flex;
  align-items: center;
  padding-left: 48px;
  position: relative;

  & + & {
    margin-top: 16px;
  }

  color: ${({ $isActive, theme }) =>
    $isActive ? theme.palette.white : theme.palette.gray400};

  ${helpers.transition("color")};

  ${DefaultIcon} {
    opacity: ${({ $isActive }) => ($isActive ? 0 : 1)};
  }

  ${ActiveIcon} {
    opacity: ${({ $isActive }) => ($isActive ? 1 : 0)};
  }

  cursor: pointer;
`;

const Text = styled.span`
  line-height: 1.2;
  font-size: 13px;
  font-weight: 500;
`;

export function Navigation({ visible }: { visible: boolean }) {
  const navigationItems = [
    {
      DefaultSvg: <InquiryDefaultIcon />,
      ActiveSvg: <InquiryActiveIcon />,
      name: "Inquiry",
      hash: "#inquiries-or-leads",
    },
    {
      DefaultSvg: <MeetingDefaultIcon />,
      ActiveSvg: <MeetingActiveIcon />,
      name: "Meeting",
      hash: "#meetings",
    },
    {
      DefaultSvg: <ContractDefaultIcon />,
      ActiveSvg: <ContractActiveIcon />,
      name: "Contract",
      hash: "#contracts",
    },
    {
      DefaultSvg: <InvoiceDefaultIcon />,
      ActiveSvg: <InvoiceActiveIcon />,
      name: "Invoice",
      hash: "#invoice",
    },
    {
      DefaultSvg: <RoadmapDefaultIcon />,
      ActiveSvg: <RoadmapActiveIcon />,
      name: "Roadmap",
      hash: "#milestone-and-progress",
    },
    {
      DefaultSvg: <DeliverableDefaultIcon />,
      ActiveSvg: <DeliverableActiveIcon />,
      name: "Deliverable",
      hash: "#deliverables",
    },
    {
      DefaultSvg: <FeedbackDefaultIcon />,
      ActiveSvg: <FeedbackActiveIcon />,
      name: "Feedback",
      hash: "#feedback",
    },
    {
      DefaultSvg: <NotificationDefaultIcon />,
      ActiveSvg: <NotificationActiveIcon />,
      name: "Notification",
      hash: "#notification",
    },
  ];

  const [activeItemName, setActiveItemName] = useState("");
  useEffect(() => {
    const observers = _.map(navigationItems, ({ name, hash }) => {
      const targetSection = document.querySelector(hash);

      const debouncedSetActiveItemName = _.debounce(
        (isIntersection: boolean, name: string) => {
          setActiveItemName(isIntersection ? name : "");
        },
        50,
        { trailing: true }
      );

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            debouncedSetActiveItemName(entry.isIntersecting, name);
          }
        },
        {
          threshold: 0.68,
        }
      );

      if (targetSection) {
        observer.observe(targetSection);
      }
      return observer;
    });

    return () => {
      _.forEach(observers, (observer) => observer.disconnect());
    };
  }, []);

  const handleClickNavItem = (hash: string) => () => {
    const targetSection = document.querySelector(hash);
    if (targetSection) {
      targetSection.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  return (
    <NavigationWrapper $visible={visible}>
      {_.map(navigationItems, ({ DefaultSvg, ActiveSvg, name, hash }, i) => (
        <NavigationItem
          key={i}
          $isActive={activeItemName === name}
          onClick={handleClickNavItem(hash)}
        >
          <DefaultIcon>{DefaultSvg}</DefaultIcon>
          <ActiveIcon>{ActiveSvg}</ActiveIcon>
          <Text>{name}</Text>
        </NavigationItem>
      ))}
    </NavigationWrapper>
  );
}
